import { DialogNavigationBlocker } from '@/components/layout/DialogNavigationBlocker';
import { Box, Dialog, Slide, SxProps, Theme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useForkRef } from '@mui/material/utils';
import clsx from 'clsx';
import {
    ForwardRefRenderFunction,
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react';

import { AxleDialogShellProps } from './AxleDialogShell.types';
import { AxleDialogShellFooter } from './AxleDialogShellFooter';
import { AxleDialogShellHeader } from './AxleDialogShellHeader';
import { DialogShellPortalHost } from './dialogShellPortal';
import { DIALOG_SHELL_PAPER_ATTRIBUTE, useDialogDrag } from './useDialogDrag';
import { useScrollShadows } from './useScrollShadows';

// Matches MUI's default 32px paper margins, so a capped paper still breathes.
const VIEWPORT_MARGIN = '64px';

const SlideTransition = forwardRef(
    ({ children, ...props }: { children: ReactElement } & TransitionProps, ref) => (
        <Slide direction={'down'} ref={ref} {...props}>
            {children}
        </Slide>
    ),
);
SlideTransition.displayName = 'SlideTransition';

/* Dialog shell with a fixed header, a scrolling body, and a fixed footer.
 *
 * The paper is a grid whose middle row is the only scrollport, so header and
 * footer stay in place however long the body gets — which is also what keeps the
 * drag handle reachable at any scroll position.
 */
const AxleDialogShellReffed: ForwardRefRenderFunction<HTMLDivElement, AxleDialogShellProps> = (
    props,
    ref,
) => {
    const {
        children,
        open,
        onClose,
        title,
        headerActions,
        header,
        footer,
        height = 'auto',
        isDraggable = false,
        dragBounds = 'contain',
        minVisibleDragSize,
        resetPositionOnDoubleClick = false,
        allowBackgroundInteraction = false,
        hideCloseButton = false,
        confirmMessageBeforeClosing,
        disableBodyPadding = false,
        disableBodyScroll = false,
        bodyRef: providedBodyRef,
        dataTestId,
        slotProps,
        className = '',
        fullScreen = false,
        fullWidth = true,
        onTransitionExited,
        sx,
        ...rest
    } = props;

    /* One host per dialog instance, so nested dialogs each portal into their own. The colons
     * React puts in a generated id are stripped, since a popout library given this id as a
     * target may look the host up with `querySelector`, where a colon is not valid.
     */
    const portalHostId = `axle-dialog-shell-portal-${useId().replace(/:/g, '')}`;

    const isDragEnabled = isDraggable && !fullScreen;
    const { handleProps, paperTranslate, resetPosition } = useDialogDrag({
        enabled: isDragEnabled,
        bounds: dragBounds,
        minVisible: minVisibleDragSize,
        resetOnDoubleClick: resetPositionOnDoubleClick,
    });
    const { bodyRef, showTopShadow, showBottomShadow } = useScrollShadows({
        disabled: disableBodyScroll,
    });
    const forkedBodyRef = useForkRef(bodyRef, providedBodyRef);

    /* A modal marks every sibling of its container `aria-hidden`, which would hide the
     * page a non-modal dialog deliberately leaves usable — from screen readers, and from
     * any query that walks the accessibility tree. Mounting into a container of its own
     * leaves it no siblings to hide.
     */
    const [ownContainer, setOwnContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        if (!allowBackgroundInteraction) {
            return;
        }
        const container = document.createElement('div');
        document.body.appendChild(container);
        setOwnContainer(container);
        return () => {
            container.remove();
            setOwnContainer(null);
        };
    }, [allowBackgroundInteraction]);

    /* Guarded close, for a dialog whose content would be lost by closing. Wrapping here
     * rather than at the caller covers every route to a close the shell owns: the close
     * button, the backdrop, and Escape. A dialog's own save path calls its own close and
     * is deliberately not guarded.
     */
    const guardedClose = useCallback(() => {
        if (
            confirmMessageBeforeClosing !== undefined &&
            !window.confirm(confirmMessageBeforeClosing)
        ) {
            return;
        }
        onClose();
    }, [confirmMessageBeforeClosing, onClose]);

    // Every open starts centered. Held until the exit transition finishes, so a
    // dragged dialog animates out from where it was left rather than snapping back.
    const onExited = useCallback(() => {
        resetPosition();
        onTransitionExited?.();
    }, [onTransitionExited, resetPosition]);

    const paperProps = useMemo(
        () => ({
            [DIALOG_SHELL_PAPER_ATTRIBUTE]: '',
            'data-test-id': dataTestId,
            // Inline rather than in `sx` because the offset changes every frame of a
            // drag, and each distinct `sx` value mints an emotion class. It also stays
            // off `transform`, which the transition owns and overwrites.
            style: { translate: paperTranslate },
            sx: {
                // Re-enables pointer events the non-modal root switches off.
                ...(allowBackgroundInteraction && { pointerEvents: 'auto' }),
                display: 'grid',
                gridTemplateRows: 'auto minmax(0, 1fr) auto',
                overflow: 'hidden',
                ...(fullScreen
                    ? {}
                    : {
                          maxHeight: `calc(100% - ${VIEWPORT_MARGIN})`,
                          ...(height === 'fill' && { height: `calc(100% - ${VIEWPORT_MARGIN})` }),
                      }),
                ...slotProps?.paper,
            } as SxProps<Theme>,
        }),
        [
            allowBackgroundInteraction,
            dataTestId,
            fullScreen,
            height,
            paperTranslate,
            slotProps?.paper,
        ],
    );

    const bodySx = useMemo(
        (): SxProps<Theme> => ({
            // Pinned to the body track so a header with display: none cannot shift it.
            gridRow: 2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            /* A grid item's automatic minimum size is its content, so a body wider than the
             * paper would widen the row past the track and be clipped by the paper. Held to
             * the track instead, so content that manages its own scrollport can scroll.
             */
            minWidth: 0,
            overflowY: disableBodyScroll ? 'visible' : 'auto',
            overscrollBehavior: 'contain',
            ...(disableBodyPadding ? {} : { px: 3, py: 2 }),
            ...slotProps?.body,
        }),
        [disableBodyPadding, disableBodyScroll, slotProps?.body],
    );

    /* The modal root and its container both cover the viewport, so they swallow
     * clicks even with the backdrop hidden. `pointerEvents: 'none'` here cascades to
     * the container, and the paper turns them back on for itself.
     */
    const rootSx = useMemo(
        (): SxProps<Theme> => ({
            ...(allowBackgroundInteraction && { pointerEvents: 'none' }),
            ...sx,
        }),
        [allowBackgroundInteraction, sx],
    );

    /* Held back for the one commit it takes to create that container: mounting into the
     * body first lets the modal mark the page `aria-hidden`, and switching containers
     * afterwards does not undo it.
     */
    if (allowBackgroundInteraction && ownContainer === null) {
        return null;
    }

    return (
        <DialogShellPortalHost id={portalHostId} isActive={open}>
            {confirmMessageBeforeClosing !== undefined && open ? (
                <DialogNavigationBlocker confirmMessage={confirmMessageBeforeClosing} />
            ) : null}
            <Dialog
                ref={ref}
                className={clsx('axle-dialog-shell', className)}
                sx={rootSx}
                scroll={'paper'}
                open={open}
                fullScreen={fullScreen}
                fullWidth={fullWidth}
                onClose={guardedClose}
                TransitionComponent={SlideTransition}
                onTransitionExited={onExited}
                PaperProps={paperProps}
                {...rest}
                /* Last, so they win over the spread: background interaction depends on the
                 * dialog having a container of its own, and on the three modal behaviors it
                 * opts out of. A caller that set any of them would leave the page behind
                 * `aria-hidden` while it stays clickable.
                 */
                {...(allowBackgroundInteraction && {
                    container: ownContainer,
                    hideBackdrop: true,
                    disableEnforceFocus: true,
                    disableScrollLock: true,
                })}
            >
                <AxleDialogShellHeader
                    title={title}
                    actions={headerActions}
                    onClose={guardedClose}
                    hideCloseButton={hideCloseButton}
                    showScrollShadow={showTopShadow}
                    dragHandleProps={isDragEnabled ? handleProps : undefined}
                    sx={slotProps?.header}
                >
                    {header}
                </AxleDialogShellHeader>
                <Box ref={forkedBodyRef} className={'axle-dialog-shell-body'} sx={bodySx}>
                    {children}
                </Box>
                {footer === undefined ? null : (
                    <AxleDialogShellFooter
                        showScrollShadow={showBottomShadow}
                        sx={slotProps?.footer}
                    >
                        {footer}
                    </AxleDialogShellFooter>
                )}
            </Dialog>
        </DialogShellPortalHost>
    );
};

export const AxleDialogShell = forwardRef(AxleDialogShellReffed);
